import React, { useCallback, useEffect, useRef, useState } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { IEcommerceQuestionSchema } from '../../types'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'
import style from './CartCounter.module.scss'

interface ICartCounterProps {
  quantity: number
  index: number
  schema: IEcommerceQuestionSchema
  onCloseCounter: () => void
  onAddToCartFromCounter: (qnt: number, index: number) => void
}
const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

const CartCounter = ({ quantity, index, onCloseCounter, onAddToCartFromCounter, schema }: ICartCounterProps) => {
  const [selectedNumber, setSelectedNumber] = useState(quantity)
  const counterRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLDivElement>>([])
  const { t } = useSurveyLocale()

  useEffect(() => {
    if (counterRef.current && itemRefs.current[quantity]) {
      requestAnimationFrame(() => {
        itemRefs.current[quantity].scrollIntoView({ block: 'center', behavior: 'auto' })
      })
    }
  }, [quantity])

  const onScroll = useCallback(() => {
    if (!counterRef.current) return
    const containerHeight = counterRef.current.clientHeight

    let closestItem = -1
    let minDistance = Infinity

    itemRefs.current.forEach((itemRef, index) => {
      if (itemRef) {
        const rect = itemRef.getBoundingClientRect()
        const counterRect = counterRef.current.getBoundingClientRect()
        const itemCenterY = rect.top + rect.height / 2 - counterRect.top
        const containerCenterY = containerHeight / 2
        const distance = Math.abs(itemCenterY - containerCenterY)

        if (distance < minDistance) {
          minDistance = distance
          closestItem = index
        }
      }
    })

    if (closestItem >= 0 && closestItem < numbers.length) {
      setSelectedNumber(numbers[closestItem])
    }
    hapticEffects.soft()
  }, [])

  const submitCounter = () => {
    hapticEffects.selection()
    onAddToCartFromCounter(selectedNumber, index)
    onCloseCounter()
  }

  return (
    <div className={style.counterLayout} onClick={onCloseCounter}>
      <div className={style.counterBody} onClick={event => event.stopPropagation()}>
        <div className={style.line} />
        <div className={style.counterWrapper}>
          <div className={style.selectorIndicator} />
          <div className={style.counter} ref={counterRef} onScroll={onScroll}>
            {0 === schema.max_qty_per_product ? (
              <>
                {numbers.map((num, index) => (
                  <div
                    className={`
                        ${style.counterItem}
                        ${num === selectedNumber ? style.selected : ''}}
                        ${0 === index ? style.counterItemFirst : ''}
                        ${numbers.length - 1 === index ? style.counterItemLast : ''}
                      `}
                    key={num}
                    ref={el => (itemRefs.current[index] = el)}
                    onClick={() => {
                      setSelectedNumber(num)
                      itemRefs.current[index].scrollIntoView({ block: 'center', behavior: 'smooth' })
                    }}
                  >
                    {0 === num ? t('questions', 'ec', 'nike', 'remove') : `${num}`}
                  </div>
                ))}
              </>
            ) : (
              <>
                {numbers.map((num, index) =>
                  index - 1 <= schema.max_qty_per_product ? (
                    <div
                      className={`
                        ${style.counterItem}
                        ${num === selectedNumber ? style.selected : ''}}
                        ${0 === index ? style.counterItemFirst : ''}
                        ${numbers.length - 1 === index ? style.counterItemLast : ''}
                        ${
                          0 !== schema.max_qty_per_product && index > schema.max_qty_per_product
                            ? style.counterItemDisabled
                            : ''
                        }
                      `}
                      key={num}
                      ref={el => (itemRefs.current[index] = el)}
                      onClick={() => {
                        setSelectedNumber(num)
                        itemRefs.current[index].scrollIntoView({ block: 'center', behavior: 'smooth' })
                      }}
                    >
                      {0 === num ? t('questions', 'ec', 'nike', 'remove') : `${num}`}
                    </div>
                  ) : null
                )}
              </>
            )}
          </div>
        </div>
        <button className={style.submit} onClick={submitCounter}>
          {t('questions', 'ec', 'nike', 'done')}
        </button>
      </div>
    </div>
  )
}

export default CartCounter
